import { useState, useEffect } from 'react';
import { Plus, Edit3, Trash2, Eye, EyeOff } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { logger } from '../../services/loggingService';
import toast from 'react-hot-toast';

interface WireframeGalleryProps {
  projectId: string;
}

export function WireframeGallery({ projectId }: WireframeGalleryProps) {
  const [wireframes, setWireframes] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadWireframes();
  }, [projectId]);

  const loadWireframes = async () => {
    try {
      setLoading(true);
      const { data } = await supabase
        .from('wireframes')
        .select('*')
        .eq('project_id', projectId);
      
      setWireframes(data || []);
    } catch (error) {
      logger.error('Error loading wireframes', { error, projectId });
      toast.error('Failed to load wireframes');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Wireframes</h3>
        <button className="btn btn-primary flex items-center gap-2">
          <Plus className="w-4 h-4" />
          Add Wireframe
        </button>
      </div>
      
      {wireframes.length === 0 ? (
        <div className="text-center py-8">
          <Eye className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">No wireframes created yet</p>
          <p className="text-sm text-gray-500 dark:text-gray-500 mt-2">
            Create wireframes to visualize your application's user interface
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {wireframes.map((wireframe) => (
            <div key={wireframe.id} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
                {wireframe.page_name}
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                {wireframe.description}
              </p>
              <div className="flex gap-2">
                <button className="btn btn-primary flex-1 px-3 py-2">
                  <Eye className="w-4 h-4 inline mr-1" />
                  View
                </button>
                <button className="px-3 py-2 bg-gray-600 text-white rounded hover:bg-gray-700">
                  <Edit3 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}